import { useEffect, useRef, useState } from 'react';
import {
  Modal, Box, Typography, IconButton, Button, Stack, TextField,
  CircularProgress, Alert, Avatar, Switch, FormControlLabel
} from '@mui/material';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import UploadOutlined from '@ant-design/icons/UploadOutlined';
import axios from 'api/axiosInstance';
import endpoints from 'api/endpoints';
import LogoAdjustModal from 'components/LogoAdjustModal';
import buildImgTransform from 'utils/imgTransform';

const outerSx = {
  position: 'absolute', top: '50%', left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '95%', sm: 560 },
  maxHeight: '92vh',
  bgcolor: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  borderRadius: 3,
  boxShadow: '0 24px 64px rgba(15, 23, 42, 0.22)',
  border: '1px solid rgba(255, 255, 255, 0.6)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  outline: 'none',
};
const headerSx = {
  flexShrink: 0,
  px: 3, py: 2,
  borderBottom: '1px solid',
  borderColor: 'divider',
  bgcolor: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(12px)',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
};
const bodySx = {
  flex: 1, overflowY: 'auto', px: 3, py: 2.5,
  bgcolor: 'rgba(248, 250, 252, 0.4)',
};
const footerSx = {
  flexShrink: 0, px: 3, py: 1.75,
  borderTop: '1px solid', borderColor: 'divider',
  bgcolor: 'rgba(255, 255, 255, 0.85)',
  backdropFilter: 'blur(12px)',
  display: 'flex', gap: 1.5, justifyContent: 'flex-end',
};

export default function DoctorProfileModal({ open, onClose, doctor, onSaved }) {
  const token = localStorage.getItem('token');
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: '', bio: '', qualifications: '', experience_years: '',
    show_on_website: true,
    in_person_enabled: true,
    online_enabled: false,
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [photoScale, setPhotoScale] = useState(1);
  const [photoPosition, setPhotoPosition] = useState('0% 0%');
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !doctor?.id) return;
    setError('');
    setForm({
      name: doctor.name || '',
      bio: doctor.bio || '',
      qualifications: doctor.qualifications || '',
      experience_years: doctor.experience_years ?? '',
      show_on_website: doctor.show_on_website !== false,
      in_person_enabled: doctor.in_person_enabled !== false,
      online_enabled: !!doctor.online_enabled,
    });
    setPhotoPreview(doctor.photo || '');
    setPhotoFile(null);
    setPhotoScale(Number(doctor.photo_scale) || 1);
    setPhotoPosition(doctor.photo_position || '0% 0%');
  }, [open, doctor?.id]);

  const handlePhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
    setPhotoScale(1);
    setPhotoPosition('0% 0%');
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('Name is required'); return; }
    if (!form.in_person_enabled && !form.online_enabled) {
      setError('Pick at least one consultation mode (In-person or Online).');
      return;
    }
    try {
      setSaving(true);
      setError('');
      const payload = new FormData();
      payload.append('name', form.name);
      payload.append('bio', form.bio || '');
      payload.append('qualifications', form.qualifications || '');
      if (form.experience_years !== '' && form.experience_years !== null) {
        payload.append('experience_years', String(form.experience_years));
      } else {
        payload.append('experience_years', '');
      }
      payload.append('show_on_website', form.show_on_website ? 'true' : 'false');
      payload.append('in_person_enabled', form.in_person_enabled ? 'true' : 'false');
      payload.append('online_enabled', form.online_enabled ? 'true' : 'false');
      if (photoFile) payload.append('photo', photoFile);
      payload.append('photo_scale', String(photoScale || 1));
      payload.append('photo_position', photoPosition || '0% 0%');
      await axios.patch(
        endpoints.company.updateDoctor(doctor.id),
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSaved?.();
      onClose?.();
    } catch (err) {
      const d = err.response?.data;
      setError(typeof d === 'object' ? Object.values(d).flat().join('. ') : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <>
    <Modal open={open} onClose={onClose}>
      <Box sx={outerSx}>
        <Box sx={headerSx}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Doctor Profile</Typography>
            <Typography variant="caption" color="text.secondary">
              Public details shown on the website
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose}><CloseOutlined /></IconButton>
        </Box>

        <Box sx={bodySx}>
          <Stack spacing={2.25}>
            {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar src={photoPreview} imgProps={{ style: { transform: buildImgTransform({ scale: photoScale, position: photoPosition }), transformOrigin: 'center' } }} sx={{ width: 88, height: 88, border: '2px solid', borderColor: 'divider' }}>
                {form.name?.charAt(0)?.toUpperCase() || '?'}
              </Avatar>
              <Stack spacing={0.5} sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined" size="small" startIcon={<UploadOutlined />}
                    onClick={() => fileRef.current?.click()}
                  >
                    {photoPreview ? 'Change Photo' : 'Upload Photo'}
                  </Button>
                  {photoPreview && (
                    <Button variant="text" size="small" onClick={() => setAdjustOpen(true)}>
                      Adjust
                    </Button>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  Square images work best. Use Adjust to reposition/zoom. Shown on the public Meet Our Doctors section.
                </Typography>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={handlePhoto} />
              </Stack>
            </Box>

            <TextField
              label="Name *" size="small" fullWidth
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />

            <TextField
              label="Qualifications" size="small" fullWidth
              placeholder="e.g. MBBS, MD (Internal Medicine)"
              helperText="Shown after the name on the public card."
              value={form.qualifications}
              onChange={(e) => setForm((p) => ({ ...p, qualifications: e.target.value }))}
            />

            <TextField
              label="Years of experience" size="small" type="number"
              placeholder="e.g. 15"
              sx={{ maxWidth: 200 }}
              value={form.experience_years}
              onChange={(e) => setForm((p) => ({ ...p, experience_years: e.target.value }))}
              inputProps={{ min: 0, max: 80 }}
            />

            <TextField
              label="Short bio" size="small" fullWidth multiline rows={4}
              placeholder="Background, philosophy, focus areas, etc."
              value={form.bio}
              onChange={(e) => setForm((p) => ({ ...p, bio: e.target.value }))}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={form.show_on_website}
                  onChange={(e) => setForm((p) => ({ ...p, show_on_website: e.target.checked }))}
                />
              }
              label={
                <Box>
                  <Typography variant="body2">Show on public website</Typography>
                  <Typography variant="caption" color="text.secondary">
                    When off, this doctor is hidden from the Meet Our Doctors section.
                  </Typography>
                </Box>
              }
            />

            <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Consultation modes</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Pick which modes this doctor offers. The booking form on the website will show only these options.
              </Typography>
              <Stack spacing={0.5}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.in_person_enabled}
                      onChange={(e) => setForm((p) => ({ ...p, in_person_enabled: e.target.checked }))}
                    />
                  }
                  label={<Typography variant="body2">🏥 In-person at branch</Typography>}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.online_enabled}
                      onChange={(e) => setForm((p) => ({ ...p, online_enabled: e.target.checked }))}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2">🎥 Online (video) consultation</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Uses your tenant-wide meeting link (set in Company Settings).
                      </Typography>
                    </Box>
                  }
                />
              </Stack>
            </Box>
          </Stack>
        </Box>

        <Box sx={footerSx}>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ minWidth: 120 }}>
            {saving ? <CircularProgress size={20} /> : 'Save Profile'}
          </Button>
        </Box>
      </Box>
    </Modal>
    <LogoAdjustModal
      open={adjustOpen}
      onClose={() => setAdjustOpen(false)}
      src={photoPreview}
      fit="cover"
      aspect="4 / 3"
      title="Adjust photo"
      label="photo"
      scale={photoScale}
      position={photoPosition}
      onApply={({ scale, position }) => { setPhotoScale(scale); setPhotoPosition(position); }}
    />
    </>
  );
}
